import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { adminGetSubscribers, adminGetPayments, adminGetSubAnalytics, adminExportSubscribers } from "@/lib/api";
import { toast } from "sonner";

export default function SubscribersAdmin() {
  const [subs, setSubs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [tab, setTab] = useState("subscribers");
  const [locFilter, setLocFilter] = useState("");

  useEffect(() => {
    adminGetSubscribers().then(setSubs).catch(() => toast.error("Failed to load subscribers"));
    adminGetPayments().then(setPayments).catch(() => {});
    adminGetSubAnalytics().then(setAnalytics).catch(() => {});
  }, []);

  const countries = [...new Set(subs.map((s) => s.country).filter(Boolean))].sort();
  const filtered = locFilter ? subs.filter((s) => s.country === locFilter) : subs;

  const exportFile = async (format) => {
    try {
      await adminExportSubscribers(format);
      toast.success(`Exported subscribers.${format}`);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div data-testid="admin-subscribers">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl text-[#1F3864] mb-1">Audience</h1>
          <p className="text-[#1F3864]/60">Newsletter subscribers, analytics, and payment records.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="export-csv" onClick={() => exportFile("csv")}
            className="inline-flex items-center gap-2 bg-white border border-[#1F3864]/20 text-[#1F3864] font-bold text-sm px-4 py-2.5 rounded-full hover:bg-[#F4F1EC]">
            <Download size={15} /> CSV
          </button>
          <button data-testid="export-xlsx" onClick={() => exportFile("xlsx")}
            className="inline-flex items-center gap-2 bg-[#1F3864] text-[#D7E85C] font-bold text-sm px-4 py-2.5 rounded-full hover:bg-[#16294B]">
            <Download size={15} /> XLSX
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        {[["subscribers", `Subscribers (${subs.length})`], ["analytics", "Analytics"], ["payments", `Payments (${payments.length})`]].map(([id, label]) => (
          <button key={id} data-testid={`tab-${id}`} onClick={() => setTab(id)}
            className={`px-4 py-2 rounded-full text-sm font-bold ${tab === id ? "bg-[#1F3864] text-[#D7E85C]" : "bg-white text-[#1F3864] border border-[#1F3864]/20"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "subscribers" && (
        <div className="bg-white rounded-2xl border border-[#1F3864]/10 overflow-x-auto">
          <div className="px-4 py-3 border-b border-[#1F3864]/10 flex items-center gap-3">
            <label className="text-xs font-bold uppercase tracking-wider text-[#1F3864]/50">Filter by country</label>
            <select data-testid="subscribers-country-filter" value={locFilter} onChange={(e) => setLocFilter(e.target.value)}
              className="border border-[#1F3864]/20 rounded-lg px-3 py-1.5 text-sm bg-white">
              <option value="">All countries</option>
              {countries.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {filtered.length === 0 ? <p className="text-center text-[#1F3864]/50 text-sm py-12" data-testid="subscribers-empty">No subscribers yet.</p> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-[#1F3864]/50 border-b border-[#1F3864]/10">
                  <th className="px-4 py-3">Email</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Country</th><th className="px-4 py-3">Source</th><th className="px-4 py-3">Subscribed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1F3864]/5">
                {filtered.map((s) => (
                  <tr key={s.id} data-testid={`sub-row-${s.id}`}>
                    <td className="px-4 py-3 font-bold text-[#1F3864]">{s.email}</td>
                    <td className="px-4 py-3 text-[#1F3864]/70">{s.phone || "—"}</td>
                    <td className="px-4 py-3 text-[#1F3864]/70">{s.country || "—"}</td>
                    <td className="px-4 py-3 text-[#1F3864]/70">{s.source}</td>
                    <td className="px-4 py-3 text-[#1F3864]/50 text-xs">{new Date(s.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "analytics" && (
        <div className="space-y-6">
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl border border-[#1F3864]/10 p-5">
              <div className="text-3xl font-display font-bold text-[#1F3864]">{analytics?.total ?? "—"}</div>
              <div className="text-xs font-bold uppercase tracking-wider text-[#1F3864]/50">Total subscribers</div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-[#1F3864]/10 p-5">
              <h3 className="font-bold text-[#1F3864] mb-3 text-sm uppercase tracking-wider">By day</h3>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {analytics && Object.entries(analytics.daily).length > 0 ? (
                  Object.entries(analytics.daily).reverse().map(([d, n]) => (
                    <div key={d} className="flex justify-between text-sm"><span className="text-[#1F3864]/70">{d}</span><span className="font-bold text-[#1F3864]">{n}</span></div>
                  ))
                ) : <p className="text-[#1F3864]/40 text-sm">No data yet.</p>}
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-[#1F3864]/10 p-5">
              <h3 className="font-bold text-[#1F3864] mb-3 text-sm uppercase tracking-wider">By month</h3>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {analytics && Object.entries(analytics.monthly).length > 0 ? (
                  Object.entries(analytics.monthly).reverse().map(([m, n]) => (
                    <div key={m} className="flex justify-between text-sm"><span className="text-[#1F3864]/70">{m}</span><span className="font-bold text-[#1F3864]">{n}</span></div>
                  ))
                ) : <p className="text-[#1F3864]/40 text-sm">No data yet.</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "payments" && (
        <div className="bg-white rounded-2xl border border-[#1F3864]/10 overflow-hidden">
          {payments.length === 0 ? <p className="text-center text-[#1F3864]/50 text-sm py-12" data-testid="payments-empty">No payments yet.</p> : (
            <div className="divide-y divide-[#1F3864]/5">
              {payments.map((p) => (
                <div key={p.session_id} className="px-4 py-3 flex flex-wrap justify-between gap-2 text-sm">
                  <span className="font-bold text-[#1F3864]">{p.package_name} · {p.currency === "php" ? "₱" : "$"}{p.amount}</span>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${p.payment_status === "paid" ? "bg-[#D7E85C]/30 text-[#5C6B12]" : "bg-[#FFB56B]/20 text-[#9A5A12]"}`}>
                    {p.payment_status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
